import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import { USER_ROLES } from '@/constants/userRoles'
import { DataGridSyncUrlManagerProvider } from '@/contexts/DataGridSyncUrlManagerContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import NavigateToError from '@/features/error/pages/NavigateToError'
import { canUserReachResourceConfigurationRoute } from '@/features/resource/configuration/utils/resourceConfigurationVisibility'
import { ResourceRouteInformation } from '@/features/resource/constants'
import BatterySteeringPage from '@/features/resource/pages/BatterySteeringPage'
import ResourceConfigurationPage from '@/features/resource/pages/ResourceConfigurationPage'
import ResourceCustomAttributesPage from '@/features/resource/pages/ResourceCustomAttributesPage'
import ResourceDetailsIndex from '@/features/resource/pages/ResourceDetailsIndex'
import ResourceDetailsPage from '@/features/resource/pages/ResourceDetailsPage'
import ResourceGeneralPage from '@/features/resource/pages/ResourceGeneralPage'
import ResourceManualSteeringPage from '@/features/resource/pages/ResourceManualSteeringPage'
import ResourceRampCurvesPage from '@/features/resource/pages/ResourceRampCurvesPage'
import ResourceSchedulerPage from '@/features/resource/pages/ResourceSchedulerPage'
import ResourcesIndex from '@/features/resource/pages/ResourcesIndex'
import ResourceSteeringPage from '@/features/resource/pages/ResourceSteeringPage'
import ResourceSupportToolboxPage from '@/features/resource/pages/ResourceSupportToolboxPage'
import SoCManagementPage from '@/features/resource/pages/SoCManagementPage'

export default function ResourcesRoutes() {
  const { loggedInUser } = useAuth()
  const { permissions } = usePermissions()

  const isAdmin = loggedInUser?.role === USER_ROLES.ADMINISTRATORS.value
  // The SoC management route mounts on the `targetSocMutation` permission alone; the per-resource
  // LOCAL + battery checks happen inside the page (routing has no resource in scope).
  const canManageTargetSoC = permissions.has('targetSocMutation')
  // Mount /configuration for anyone the navigation-side predicate can route here — admins AND
  // traders. The per-resource part of `isResourceConfigurationTabVisible` (LOCAL + battery)
  // happens inside the page itself; routing has no resource in scope. Pre-iter-29 this was
  // admin-only, so traders saw the tab + got auto-redirected to /configuration only to land on
  // the wildcard 404. Keep Support Toolbox `isAdmin`-only — different audience.
  const canEditResourceConfiguration = canUserReachResourceConfigurationRoute(loggedInUser)

  return (
    <Routes>
      <Route
        element={
          <DataGridSyncUrlManagerProvider>
            <ResourcesIndex />
          </DataGridSyncUrlManagerProvider>
        }
        path={ResourceRouteInformation.INDEX.routePath}
      />

      <Route element={<ResourceDetailsPage />} path={ResourceRouteInformation.RESOURCE_DETAILS.routePath}>
        <Route index element={<ResourceDetailsIndex />} />
        <Route element={<ResourceGeneralPage />} path={ResourceRouteInformation.RESOURCE_GENERAL.routePath} />
        <Route element={<ResourceSteeringPage />} path={ResourceRouteInformation.RESOURCE_STEERING.routePath} />
        <Route element={<ResourceRampCurvesPage />} path={ResourceRouteInformation.RESOURCE_RAMP_CURVES.routePath} />
        <Route element={<BatterySteeringPage />} path={ResourceRouteInformation.RESOURCE_BATTERY_STEERING.routePath} />
        <Route
          element={<ResourceManualSteeringPage />}
          path={ResourceRouteInformation.RESOURCE_MANUAL_STEERING.routePath}
        />
        <Route
          element={<ResourceCustomAttributesPage />}
          path={ResourceRouteInformation.RESOURCE_CUSTOM_ATTRIBUTES.routePath}
        />
        {/* Legacy /local-control URL — no page component is mounted here any more; LocalControlRedirect
         * sends stale links to the resource's General tab via a client-side <Navigate replace>, not an
         * HTTP 301. */}
        <Route element={<LocalControlRedirect />} path={ResourceRouteInformation.RESOURCE_LOCAL_CONTROL.routePath} />
        {isAdmin && (
          <Route
            element={<ResourceSupportToolboxPage />}
            path={ResourceRouteInformation.RESOURCE_SUPPORT_TOOLBOX.routePath}
          />
        )}
        {canEditResourceConfiguration && (
          <Route
            element={<ResourceConfigurationPage />}
            path={ResourceRouteInformation.RESOURCE_CONFIGURATION.routePath}
          />
        )}
        {canManageTargetSoC && (
          <Route element={<SoCManagementPage />} path={ResourceRouteInformation.RESOURCE_SOC_MANAGEMENT.routePath} />
        )}
        <Route element={<NavigateToError statusCode={404} />} path="*" />
      </Route>

      <Route element={<ResourceSchedulerPage />} path={ResourceRouteInformation.RESOURCE_SCHEDULER.routePath} />

      <Route element={<NavigateToError statusCode={404} />} path="*" />
    </Routes>
  )
}

function LocalControlRedirect() {
  const { resourceUuid } = useParams()

  // Unreachable at runtime — the route only matches when the `:resourceUuid` segment is present, and a
  // dynamic segment never matches empty. Kept because `useParams()` is typed `string | undefined` and
  // `navigationPath` wants a `string`; bailing out beats asserting non-null.
  if (!resourceUuid) {
    return <NavigateToError statusCode={404} />
  }

  // LC-405: always the General tab, for every role and control mode. This used to branch on the
  // Resource configuration visibility predicate, which meant waiting for the resource to load before
  // redirecting; with a single destination there is nothing to inspect, so the redirect fires
  // immediately. /general is mounted unconditionally, and ResourceGeneralCard returns null until the
  // resource arrives, so redirecting before the fetch resolves is safe.
  return <Navigate replace to={ResourceRouteInformation.RESOURCE_GENERAL.navigationPath(resourceUuid)} />
}
